<template>
  <h2>{{ year }}</h2>
  <div class="row">
    <div class="d-none d-md-block offset-md-4 col-md-2">Team</div>
    <div class="d-none d-md-block col-md-1" @click="sortBy('points')">PF</div>
    <div class="d-none d-md-block col-md-1" @click="sortBy('pointsAgainst')">
      PA
    </div>
  </div>
  <div class="row" style="margin-bottom: 10px;" v-for="team in teams" :key="team.id">
    <div class="col-12">
      <div class="row">
        <div class="col-9 offset-md-4 col-md-2" style="text-align: left;">
          {{ team.location }} {{ team.nickname }}
        </div>
        <div class="d-none d-md-block col-3 col-md-1">
          {{ team.points }}
        </div>
        <div class="d-none d-md-block col-3 col-md-1">
          {{ team.pointsAgainst }}
        </div>
        <div class="d-md-none col-3">
          {{team.points}}
        </div>
      </div>
      <div class="row">
        <div class="col-9 offset-md-4 col-md-2 text--secondary" style="font-size: 12px; text-align: left;">
          {{team.owner.firstName}} {{team.owner.lastName}}
        </div>
        <div class="d-md-none col-3 text--secondary" style="font-size: 12px;">
          {{team.pointsAgainst}}
        </div>
      </div>
    </div>
  </div>
</template>


<script lang="ts">
import { PropType, ref } from "vue";
import { getNames } from "../use/owner-mapping";
import { seasonApi } from "@/shared/seasons/season-api";
import Season from "@/shared/seasons/season";
import Team from "@/shared/teams/team";
import Matchup from "@/shared/matchups/matchup";
import { orderBy } from "lodash";
import { useStore } from 'vuex';
import { watch } from 'vue';
type PointCounter = { [index: number]: number }

function getTeamPointsForByMatchup(matchup: Matchup, teamPointsFor: PointCounter) {
  if (matchup.away && teamPointsFor[matchup.away.teamId]) {
    teamPointsFor[matchup.away.teamId] += matchup.away.totalPoints;
  } else if (matchup.away) {
    teamPointsFor[matchup.away.teamId] = matchup.away.totalPoints;
  }

  if (teamPointsFor[matchup.home.teamId]) {
    teamPointsFor[matchup.home.teamId] += matchup.home.totalPoints;
  } else {
    teamPointsFor[matchup.home.teamId] = matchup.home.totalPoints;
  }
}

function getTeamPointsAgainstByMatchup(matchup: Matchup, teamPointsAgainst: PointCounter) {
  if (matchup.away && teamPointsAgainst[matchup.away.teamId]) {
    teamPointsAgainst[matchup.away.teamId] += matchup.home.totalPoints;
  } else if (matchup.away) {
    teamPointsAgainst[matchup.away.teamId] = matchup.home.totalPoints;
  }

  if (matchup.away && teamPointsAgainst[matchup.home.teamId]) {
    teamPointsAgainst[matchup.home.teamId] += matchup.away.totalPoints;
  } else if (matchup.away) {
    teamPointsAgainst[matchup.home.teamId] = matchup.away.totalPoints;
  }
}

function calculateSeasonData(season: Season) {
  const teamPointsFor = {};
  const teamPointsAgainst = {};
  for (let i = 0; i < season.schedule.length; i++) {
    const matchup: Matchup = season.schedule[i];
    getTeamPointsForByMatchup(matchup, teamPointsFor);
    getTeamPointsAgainstByMatchup(matchup, teamPointsAgainst);
  }

  return {
    teamPointsFor,
    teamPointsAgainst,
  };
}

function setUpTeamsForCalculations(season: Season, teams: Team[]) {
  for (let i = 0; i < season.teams.length; i++) {
    season.teams[i].points = 0;
    teams.push(season.teams[i]);
  }
}

function useSeasons(year: number) {
  let currentSortDirection = "";
  const season = ref<Season>();
  const teams = ref<Team[]>([]);
  const store = useStore();

  function sortBy(column: string) {
    if (currentSortDirection === "desc") {
      teams.value = orderBy(teams.value, column, "asc");
      currentSortDirection = "asc";
    } else {
      teams.value = orderBy(teams.value, column, "desc");
      currentSortDirection = "desc";
    }
  }

  async function loadSeason(season: number) {
    let data = await seasonApi.getSeasonCurrent(store.state.leagueId);
    let calculatedData: any = {};

    if (season != data.seasonId) {
      data = await seasonApi.getSeason(store.state.leagueId, season);
    }

    setUpTeamsForCalculations(data, teams.value);
    calculatedData = calculateSeasonData(data);

    for (let i = 0; i < teams.value.length; i++) {
      teams.value[i].points = calculatedData.teamPointsFor[
        teams.value[i].id
      ].toFixed(2);

      teams.value[i].pointsAgainst = calculatedData.teamPointsAgainst[
        teams.value[i].id
      ].toFixed(2);
    }

    teams.value = getNames(data.members, teams.value);
    sortBy("points");
  }

  watch(
    () => store.state.leagueId,
    () => {
      teams.value = [];
      loadSeason(year);
    }
  )

  loadSeason(year);

  return {
    season,
    sortBy,
    teams,
  };
}

export default {
  props: {
    year: { type: Object as PropType<number>, required: true },
  },
  setup(props: any) {
    return useSeasons(props.year);
  },
};
</script>